import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Button,
} from '@mui/material';
import {
  Gavel as ModerationIcon,
  Assessment as StatsIcon,
  Rule as RulesIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Flag as FlagIcon,
  Archive as ArchiveIcon,
} from '@mui/icons-material';
import { useUnifiedTranslation } from '../../hooks/useUnifiedTranslation';
import { managerApi } from '../../services/api/manager.api';
import { DataTable } from '../ui/DataTable';
import { formatShortDate } from '../../utils/date-utils';
import { safeParseApiError } from '../../utils/api-error';
import { PAGE_SIZE } from '../../constants/pagination';
import { ModerationStatus } from '../../types/moderation';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`moderator-tabpanel-${index}`}
      aria-labelledby={`moderator-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export const ContentModeratorDashboard: React.FC = () => {
  const { t } = useUnifiedTranslation({ namespace: 'dashboard' });
  const ts = (key: string, fallback: string): string => { const v = t(key); return typeof v === 'string' ? v : fallback; };
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState(0);
  const [selectedContent, setSelectedContent] = useState<string[]>([]);

  // Загрузка контента для модерации
  const { data: contentData, isLoading: isLoadingContent, refetch: _refetchContent } = useQuery({
    queryKey: ['moderator', 'content'],
    queryFn: async () => {
      const response = await managerApi.getContent({ 
        status: ModerationStatus.PENDING,
        limit: PAGE_SIZE.LARGE 
      });
      return response.data;
    },
    refetchInterval: 30000,
  });

  // Загрузка всего контента (для статистики)
  const { data: allContentData } = useQuery({
    queryKey: ['moderator', 'all-content'],
    queryFn: async () => {
      const response = await managerApi.getContent({ limit: PAGE_SIZE.MAX });
      return response.data;
    },
  });

  // Мутация действия с контентом
  const contentActionMutation = useMutation({
    mutationFn: ({ contentId, action }: { contentId: string; action: any }) =>
      managerApi.performContentAction(contentId, { action }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['moderator'] });
      alert('Действие выполнено!');
    },
    onError: (error) => {
      console.error('Ошибка:', error);
      alert('Ошибка при выполнении действия');
    },
  });

  // Мутация массового действия
  const bulkActionMutation = useMutation({
    mutationFn: ({ contentIds, action }: { contentIds: string[]; action: any }) =>
      managerApi.performBulkContentAction({ contentIds, action }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['moderator'] });
      setSelectedContent([]);
      alert('Массовое действие выполнено!');
    },
    onError: (error) => {
      console.error('Ошибка массового действия:', error);
      alert(`Не удалось выполнить массовое действие: ${safeParseApiError(error).message}`);
    },
  });

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleContentAction = (contentId: string, action: 'reject' | 'flag' | 'archive') => {
    contentActionMutation.mutate({ contentId, action });
  };

  const handleBulkAction = (action: 'reject' | 'flag' | 'archive' | 'delete') => {
    if (selectedContent.length === 0) {
      alert('Выберите контент для действия');
      return;
    }
    // Используем window.confirm для обхода eslint правила
    if (window.confirm(`Выполнить действие "${action}" для ${selectedContent.length} элементов?`)) {
      bulkActionMutation.mutate({ contentIds: selectedContent, action });
    }
  };

  // Статистика
  const stats = {
    pending: allContentData?.content?.filter((c: any) => c.status === 'pending').length || 0,
    approved: allContentData?.content?.filter((c: any) => c.status === 'approved').length || 0,
    rejected: allContentData?.content?.filter((c: any) => c.status === 'rejected').length || 0,
    flagged: allContentData?.content?.filter((c: any) => c.status === 'flagged').length || 0,
    total: allContentData?.total || 0,
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Заголовок */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          {ts('contentModerator.title', 'Модерация контента')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {ts('contentModerator.subtitle', 'Проверка и управление пользовательским контентом')}
        </Typography>
      </Box>

      {/* KPI карточки */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {ts('contentModerator.kpis.pending', 'На модерации')}
                  </Typography>
                  <Typography variant="h4">
                    {stats.pending}
                  </Typography>
                </Box>
                <ModerationIcon color="warning" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {ts('contentModerator.kpis.approved', 'Одобрено')}
                  </Typography>
                  <Typography variant="h4">
                    {stats.approved}
                  </Typography>
                </Box>
                <ApproveIcon color="success" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {ts('contentModerator.kpis.rejected', 'Отклонено')}
                  </Typography>
                  <Typography variant="h4">
                    {stats.rejected}
                  </Typography>
                </Box>
                <RejectIcon color="error" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {ts('contentModerator.kpis.flagged', 'Помечено')}
                  </Typography>
                  <Typography variant="h4">
                    {stats.flagged}
                  </Typography>
                </Box>
                <FlagIcon color="info" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Вкладки */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label={ts('contentModerator.tabs.content', 'Контент')} icon={<ModerationIcon />} iconPosition="start" />
            <Tab label={ts('contentModerator.tabs.statistics', 'Статистика')} icon={<StatsIcon />} iconPosition="start" />
            <Tab label={ts('contentModerator.tabs.rules', 'Правила')} icon={<RulesIcon />} iconPosition="start" />
          </Tabs>
        </Box>

        {/* Вкладка: Контент */}
        <TabPanel value={activeTab} index={0}>
          {selectedContent.length > 0 && (
            <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
              <Button
                size="small"
                color="error"
                startIcon={<RejectIcon />}
                onClick={() => handleBulkAction('reject')}
              >
                Отклонить выбранные
              </Button>
              <Button
                size="small"
                color="warning"
                startIcon={<FlagIcon />}
                onClick={() => handleBulkAction('flag')}
              >
                Пометить выбранные
              </Button>
            </Box>
          )}

          {isLoadingContent ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : contentData?.content && contentData.content.length > 0 ? (
            <DataTable
              title="Контент на модерации"
              columns={[
                {
                  id: 'type',
                  label: 'Тип',
                  render: (row: any) => <Chip label={row.type} size="small" />,
                },
                {
                  id: 'title',
                  label: 'Заголовок',
                  sortable: true,
                },
                {
                  id: 'authorName',
                  label: 'Автор',
                },
                {
                  id: 'priority',
                  label: 'Приоритет',
                  render: (row: any) => (
                    <Chip
                      label={row.priority}
                      size="small"
                      color={
                        row.priority === 'high' ? 'error' :
                        row.priority === 'medium' ? 'warning' : 'default'
                      }
                    />
                  ),
                },
                {
                  id: 'reportCount',
                  label: 'Жалоб',
                },
                {
                  id: 'createdAt',
                  label: 'Создан',
                  render: (row: any) => formatShortDate(row.createdAt),
                },
                {
                  id: 'actions',
                  label: 'Действия',
                  render: (row: any) => (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleContentAction(row.id, 'reject')}
                        title="Отклонить"
                      >
                        <RejectIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="warning"
                        onClick={() => handleContentAction(row.id, 'flag')}
                        title="Пометить"
                      >
                        <FlagIcon />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleContentAction(row.id, 'archive')}
                        title="Архивировать"
                      >
                        <ArchiveIcon />
                      </IconButton>
                    </Box>
                  ),
                },
              ]}
              data={contentData.content}
              rowKey={(row: any) => row.id}
              pagination
              selectable
              searchable
            />
          ) : (
            <Alert severity="success">
              Нет контента для модерации
            </Alert>
          )}
        </TabPanel>

        {/* Вкладка: Статистика */}
        <TabPanel value={activeTab} index={1}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Обзор модерации
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography>Всего контента:</Typography>
                      <Typography fontWeight="bold">{stats.total}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography>На модерации:</Typography>
                      <Typography fontWeight="bold" color="warning.main">{stats.pending}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography>Одобрено:</Typography>
                      <Typography fontWeight="bold" color="success.main">{stats.approved}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography>Отклонено:</Typography>
                      <Typography fontWeight="bold" color="error.main">{stats.rejected}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography>Помечено:</Typography>
                      <Typography fontWeight="bold" color="info.main">{stats.flagged}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Эффективность
                  </Typography>
                  <Box sx={{ mt: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography>Процент одобрения:</Typography>
                      <Typography fontWeight="bold">
                        {stats.total > 0 ? ((stats.approved / stats.total) * 100).toFixed(1) : 0}%
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography>Процент отклонения:</Typography>
                      <Typography fontWeight="bold">
                        {stats.total > 0 ? ((stats.rejected / stats.total) * 100).toFixed(1) : 0}%
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography>В очереди:</Typography>
                      <Typography fontWeight="bold">
                        {stats.total > 0 ? ((stats.pending / stats.total) * 100).toFixed(1) : 0}%
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        {/* Вкладка: Правила */}
        <TabPanel value={activeTab} index={2}>
          <Alert severity="info">
            <Typography variant="subtitle2" gutterBottom>
              Правила модерации контента
            </Typography>
            <Typography variant="body2">
              1. Проверять контент на соответствие требованиям платформы<br />
              2. Отклонять контент с нарушениями правил<br />
              3. Помечать сомнительный контент для дальнейшей проверки<br />
              4. Архивировать устаревший контент<br />
              5. Реагировать на жалобы пользователей в течение 24 часов
            </Typography>
          </Alert>
        </TabPanel>
      </Card>
    </Box>
  );
};

export default ContentModeratorDashboard;

